/**
 * Fantasy Console 0 - Theme Selection Modal
 */

import { useCallback, useEffect, useRef } from 'react'
import { useTheme } from '../../context/ThemeContext'
import { getAllThemes, type Theme } from '../../utils/theme'
import { CloseIcon } from '../shared/icons'
import styles from './ThemeModal.module.css'
import modalStyles from '../../styles/modal.module.css'

interface ThemeModalProps {
  isOpen: boolean
  onClose: () => void
}

const THEME_DESCRIPTIONS: Record<string, string> = {
  dark: 'Dark background with light text',
  light: 'Light background with dark text',
  monokai: 'Classic warm dark theme with vivid colors',
  'solarized-dark': 'Precision colors on teal-tinted dark background',
  nord: 'Arctic, north-bluish clean dark theme',
  warm: 'Soft warm tones on a paper-like background',
  dracula: 'Purple-accented dark theme with vivid colors',
  ayu: 'Warm dark theme with orange accents',
  'tokyo-night': 'Muted blues and purples on a dark background',
  'rose-pine': 'Muted rose and pine tones on a dark background',
  matcha: 'Soft green tea tones on a pale background',
  sand: 'Warm sandy tones on a desert-inspired background',
  braun: 'Clean tan background with precise pop colors',
  lavender: 'Soft purple tones on a pale violet background',
  'mono-dark': 'Grayscale dark theme, no color accents',
  'mono-light': 'Grayscale light theme, no color accents',
}

function ThemePreview({ theme }: { theme: Theme }) {
  const { colors } = theme
  const swatches = [
    colors.bgPrimary,
    colors.bgSecondary,
    colors.bgTertiary,
    colors.accentPrimary,
    colors.accentSecondary,
    colors.accentHighlight,
    colors.accentError,
  ]

  return (
    <div className={styles.themePreview}>
      {swatches.map((color, i) => (
        <div
          key={i}
          className={styles.previewSwatch}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  )
}

export function ThemeModal({ isOpen, onClose }: ThemeModalProps) {
  const { theme: currentTheme, setThemeId } = useTheme()
  const modalRef = useRef<HTMLDivElement>(null)
  const themes = getAllThemes()

  useEffect(() => {
    if (isOpen) {
      modalRef.current?.focus()
    }
  }, [isOpen])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  const handleSelect = useCallback(
    (id: string) => {
      setThemeId(id)
    },
    [setThemeId],
  )

  if (!isOpen) return null

  return (
    <div
      className={modalStyles.modalOverlay}
      onClick={onClose}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={modalRef}
        className={`${modalStyles.modalDialog} ${styles.modal}`}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <div className={modalStyles.modalHeader}>
          <h3 className={modalStyles.modalTitle}>Theme</h3>
          <button className={modalStyles.modalCloseButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className={`${modalStyles.modalContent} ${styles.content}`}>
          <div className={styles.themeList}>
            {themes.map((t) => (
              <button
                key={t.id}
                className={`${styles.themeOption} ${t.id === currentTheme.id ? styles.active : ''}`}
                onClick={() => handleSelect(t.id)}
              >
                <div className={styles.themeInfo}>
                  <span className={styles.themeName}>{t.name}</span>
                  <span className={styles.themeDescription}>
                    {THEME_DESCRIPTIONS[t.id] ??
                      (t.isDark ? 'Dark theme' : 'Light theme')}
                  </span>
                </div>
                <ThemePreview theme={t} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
